import React from 'react';
import CogIcon from './CogIcon';

// A one-time, first-launch coach overlay shown on the orb screen. Interactive
// steps ('tap', 'rhythm') advance when the user actually taps the orbs (counted
// in App.js); the rest advance via their CTA button. The scrim is click-through
// so the live orbs underneath stay tappable during the demo.
export const DEMO_STEPS = [
  {
    key: 'intro',
    title: 'Welcome to TapTap',
    body: 'A quick tour of how it works — it only takes a moment.',
    cta: 'Show me',
  },
  {
    key: 'tap',
    title: 'Tap the orbs',
    body: 'Tap each glowing orb. It fades away, then gently returns a moment later.',
    hint: 'Tap both orbs to continue…',
  },
  {
    key: 'rhythm',
    title: 'Find your rhythm',
    body: 'Now tap left… right… left… right. Let it settle into a slow, steady rhythm — that left-right flow is the whole idea.',
    hint: 'Keep tapping…',
  },
  {
    key: 'settings',
    title: 'Make it yours',
    body: 'Open Settings (top-right) and change all four highlighted controls — colour, size, spacing and speed.',
    hint: 'Change all 4 highlighted controls to continue…',
    pointer: 'settings',
    showCog: true,
  },
  // A short, silent pause on the orb screen so the user can enjoy the settings
  // they just chose. No caption (the Demo renders nothing for this step).
  { key: 'admire' },
  {
    key: 'stats',
    title: 'See your progress',
    body: 'Tap the S button, or swipe ↘ toward the bottom-right, to open your stats.',
    hint: 'Tap S or swipe ↘ to open your stats…',
    pointer: 'stats',
  },
  // Last feature preview before the closing Pro overview — the flashiest one,
  // so it leads straight into "here's everything Pro unlocks". Interactive:
  // opens Settings and flips the real Kids Mode switch (allowed during the
  // demo even without Pro — see isKidsPreview in App.js), same as the
  // 'settings' step above.
  {
    key: 'kids',
    title: 'For little ones',
    body: 'Open Settings (pulsing) and switch on Kids Mode — the orbs become animals with their own playful sounds. It’s part of TapTap Pro, and yours to try right now.',
    hint: 'Open Settings, then switch on Kids Mode…',
    pointer: 'settings',
    showCog: true,
    pro: true,
  },
  {
    key: 'pro',
    title: 'More ways to use TapTap',
    body: 'TapTap Pro adds five guided routines, Kids Mode and more colours. You’ll find routines on Home, and Kids Mode and colours in Settings.',
  },
];

const Pointer = ({ where }) => {
  if (where === 'settings') {
    return (
      <div
        className="absolute z-demo-highlight pointer-events-none flex items-center justify-center"
        style={{
          // Centred on the actual cog button: its container sits at
          // safe-area + 1rem, and the 44px (2.75rem) button adds another
          // half-width (1.375rem) to reach its centre.
          top: 'calc(env(safe-area-inset-top, 0px) + 2.375rem)',
          right: 'calc(env(safe-area-inset-right, 0px) + 2.375rem)',
        }}
      >
        <span className="absolute w-24 h-24 rounded-full bg-gradient-to-br from-blue-400/50 via-fuchsia-400/40 to-purple-500/50 blur-lg" />
        <span className="absolute w-24 h-24 rounded-full border-4 border-fuchsia-300/90 animate-ping" />
        <span className="absolute w-16 h-16 rounded-full border-2 border-white/80" />
      </div>
    );
  }
  // stats: bouncing arrow toward the bottom-right corner
  return (
    <div
      className="absolute z-demo-highlight pointer-events-none"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
        right: 'calc(env(safe-area-inset-right, 0px) + 1.5rem)',
      }}
    >
      <span className="block text-4xl text-white/80 animate-bounce">↘</span>
    </div>
  );
};

const Demo = ({ step, onAdvance, onSkip, onContinueFree, onSeePro, kidsActive = false }) => {
  let s = DEMO_STEPS[step];
  if (!s || !s.title) return null; // e.g. the silent 'admire' pause
  const isProIntroduction = s.key === 'pro';

  // Once Kids Mode is actually on, the caption switches from "go switch it
  // on" to "now tap them", and drops the cog pointer/icon (the Pro badge
  // stays — it's still a reminder this is a paid feature they're previewing).
  if (s.key === 'kids' && kidsActive) {
    s = {
      ...s,
      body: 'The orbs are now animals — tap them and hear each one’s playful sound!',
      hint: 'Tap the animals…',
      pointer: undefined,
      showCog: false,
    };
  }

  // Number only the steps that actually show a card (the silent 'admire' pause
  // has no title), so "3 of 6" and the dots stay in step with what's on screen.
  const visibleSteps = DEMO_STEPS.filter((st) => st.title);
  const totalVisible = visibleSteps.length;
  const currentVisible = DEMO_STEPS.slice(0, step + 1).filter((st) => st.title).length;

  return (
    <div
      className="fixed inset-0 z-demo pointer-events-none flex flex-col justify-end items-center"
      style={{
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Darken top & bottom for legibility; the transparent middle keeps the
          orbs visible and tappable through the click-through scrim. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/85" />

      {s.pointer && <Pointer where={s.pointer} />}

      {/* Card is bottom-centred (capped width) in portrait; in landscape it
          becomes a wide, short bar along the very bottom so it sits clear below
          the lifted orbs rather than covering them. On the 'stats' step it sits
          higher still, so the bouncing ↘ arrow has its own clear corner instead
          of being crowded against the card's edge. */}
      <div className={`relative w-full max-w-md m-4 ${s.key === 'stats' ? 'mb-24' : 'mb-8'} p-5 landscape:max-w-3xl landscape:mx-6 landscape:my-0 landscape:mb-3 landscape:p-3 rounded-3xl landscape:rounded-2xl bg-slate-900/90 backdrop-blur-xl border border-white/15 shadow-2xl pointer-events-auto`}>
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="truncate font-extrabold text-lg bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">{s.title}</h3>
            {s.pro && (
              <span className="shrink-0 rounded-full border border-violet-300/40 bg-violet-500/20 px-2 py-0.5 text-[0.5625rem] font-bold uppercase tracking-widest text-violet-200">
                Pro
              </span>
            )}
          </div>
          <span className="shrink-0 text-[0.6875rem] font-bold text-white bg-gradient-to-r from-blue-500/60 to-purple-500/60 border border-white/25 rounded-full px-2.5 py-1 shadow-sm">
            {currentVisible} of {totalVisible}
          </span>
        </div>
        <div className="flex items-start gap-3">
          {s.showCog && (
            <span className="shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center bg-black/40 border border-white/15">
              <CogIcon className="w-5 h-5" />
            </span>
          )}
          <p className="text-gray-300 text-sm leading-relaxed landscape:text-xs landscape:leading-snug">{s.body}</p>
        </div>

        {isProIntroduction ? (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 landscape:mt-2">
            <button
              type="button"
              onClick={onContinueFree}
              className="min-h-11 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-purple-500/20 transition-transform active:scale-[.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            >
              Continue with Free Tap
            </button>
            <button
              type="button"
              onClick={onSeePro}
              aria-haspopup="dialog"
              className="min-h-11 rounded-xl border border-violet-300/20 bg-white/[0.07] px-4 py-2.5 text-sm font-bold text-violet-100 transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            >
              See what’s in Pro
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-4 landscape:mt-2">
            <div className="flex gap-1.5" aria-hidden="true">
              {visibleSteps.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentVisible - 1 ? 'bg-white' : 'bg-white/30'}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-4">
              <button type="button" onClick={onSkip} className="min-h-11 min-w-11 text-white/65 hover:text-white/80 text-xs font-semibold transition-colors">
                Skip
              </button>
              {s.cta ? (
                <button
                  type="button"
                  onClick={onAdvance}
                  className="px-5 py-2 rounded-xl text-white text-sm font-bold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 active:scale-95 transition-all shadow-lg shadow-purple-500/25"
                >
                  {s.cta}
                </button>
              ) : (
                <span className="text-indigo-300 text-xs font-semibold animate-pulse">{s.hint}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Demo;
